import { fecha, fechaHora, useTraduccion } from '@tallerya/i18n';
import { Boton, Buscador, Chip, Filtro, IconoMas, Listado, useBusquedaDiferida, type Columna } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { usePermisos } from '../../permisos/usePermiso';
import { useSucursal } from '../../sucursal/ProveedorSucursal';
import { useAsignables, useListadoOrdenes, useEstados, type FiltrosListado, type OrdenEnListado, type PeriodoListado } from './datos';
import { descripcionDe } from './ElegirVehiculo';

// El listado de órdenes (#139): «Órdenes de servicio - Listado» (`188:8526`),
// sobre el backend de #138. Sigue el molde de `ListadoClientes`: **los filtros viven en
// la URL**, para que volver de la ficha los encuentre puestos; la página no —la
// pila de «Anterior» no cabe en un enlace— y se vuelve a la primera.
//
// La sucursal es la del app bar, como en el alta: el listado es el de donde se
// está trabajando, y «Asignado» ofrece a la gente de esa sucursal —solo a quien
// tiene `order.edit`, que es lo que pide esa lista en la API—. **Buscar pasa
// por encima de la sucursal y del estado** —una placa trae todo su historial—,
// y entonces aparece la columna Sucursal.
//
// Lo que no sigue al Figma, a propósito:
//
//   - Sin columna Total: el listado lo ve también quien no tiene
//     `order.amount.view`, y la API no trae montos en ella (#136).
//   - Sin la vista «Por mecánico» ni el menú de fila: el issue no los pide, y
//     tocar la fila ya abre la ficha, donde están todas las acciones.
//   - «Abierta» con día y hora, no «hace 2 h»: todavía no hay un formato
//     relativo en `packages/i18n`.
//   - Un filtro de fecha de apertura que el Figma no tiene y el issue sí pide.

/** Los filtros de la URL. Los parámetros se llaman como los campos: `buscar`, `estado`… */
type FiltrosUrl = Omit<FiltrosListado, 'cursor'>;

/** Lo que no está en la URL. Un parámetro con su valor inicial no se escribe. */
const FILTROS_INICIALES: FiltrosUrl = {
  buscar: '',
  estado: 'activas',
  asignado: 'todos',
  periodo: 'cualquiera',
};

const PERIODOS: readonly string[] = ['cualquiera', 'hoy', 'semana', 'mes'] satisfies PeriodoListado[];

function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const periodo = params.get('periodo') ?? '';
  return {
    buscar: params.get('buscar') ?? '',
    estado: params.get('estado') ?? FILTROS_INICIALES.estado,
    asignado: params.get('asignado') ?? FILTROS_INICIALES.asignado,
    periodo: PERIODOS.includes(periodo) ? (periodo as PeriodoListado) : FILTROS_INICIALES.periodo,
  };
}

/**
 * Un id de la URL que no está entre las opciones —un enlace viejo, o un mecánico
 * de la sucursal anterior— cuenta como el valor inicial. Mientras las opciones
 * cargan, se usa tal cual.
 */
function entre(valor: string, fijos: readonly string[], ids: readonly string[] | undefined, inicial: string) {
  return fijos.includes(valor) || ids === undefined || ids.includes(valor) ? valor : inicial;
}

export function ListadoOrdenes() {
  const { t } = useTraduccion();
  useTituloPantalla(t('admin.ordenes.titulo'));
  const { actual, cargando } = useSucursal();

  if (cargando) return <p className="entrada">{t('app.cargando')}</p>;

  if (!actual) {
    return (
      <section className="aviso">
        <h2>{t('admin.ordenes.sinSucursal.titulo')}</h2>
        <p>{t('admin.ordenes.listado.sinSucursal')}</p>
      </section>
    );
  }

  // Cambiar de sucursal arriba empieza de nuevo: el asignado elegido es de la otra.
  // El de la URL lo descarta `entre`.
  return <Contenido key={String(actual.id)} sucursal={String(actual.id)} />;
}

function Contenido({ sucursal }: { sucursal: string }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const { puede } = usePermisos();
  const estados = useEstados();
  const puedeAsignar = puede('order.edit');
  const asignables = useAsignables(puedeAsignar ? sucursal : null);

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  // La API solo sabe ir hacia adelante: volver es sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);

  // Cuando la URL cambia —un filtro, «Atrás», o tocar «Órdenes de servicio» en el
  // menú— se vuelve a la primera página, como en Clientes. La caja solo se pisa
  // si el texto llegó de afuera: el que se está escribiendo puede terminar en un
  // espacio que la URL no guarda.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar && url.buscar !== texto.trim()) setTexto(url.buscar);
  }

  /**
   * Reemplaza la entrada del historial del navegador: «Atrás» sale del listado, no
   * deshace un filtro por tecla.
   */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          if (valor && valor !== FILTROS_INICIALES[clave as keyof FiltrosUrl]) nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar: buscar.trim() }), { pendiente: texto.trim() !== url.buscar });

  const filtros: FiltrosListado = {
    ...url,
    estado: entre(url.estado, ['activas', 'todos'], estados.data?.map((e) => String(e.id)), FILTROS_INICIALES.estado),
    asignado: puedeAsignar
      ? entre(url.asignado, ['todos'], asignables.data?.map((p) => String(p.id)), FILTROS_INICIALES.asignado)
      : FILTROS_INICIALES.asignado,
    cursor,
  };
  const consulta = useListadoOrdenes(sucursal, filtros);
  const abrir = (o: OrdenEnListado) => void navegar(`/ordenes/${String(o.id)}`, { state: { desdeListado: true } });
  const pagina = consulta.data;
  // «Vencida» respecto a cuándo llegó la página: la misma hora para todas las filas.
  const ahora = consulta.dataUpdatedAt;

  const columnas: Columna<OrdenEnListado>[] = [
    {
      id: 'folio',
      titulo: t('admin.ordenes.listado.columna.folio'),
      ancho: 170,
      celda: (o) => (
        <span className="listado-ordenes__folio">
          {/* Un botón además de la fila: un `<tr>` no recibe el foco del teclado. */}
          <button type="button" className="tipos__enlace" onClick={() => abrir(o)}>
            {o.orderNumber ?? (
              <span className="orden__marca orden__marca--neutro">{t('admin.ordenes.ficha.sinFolio')}</span>
            )}
          </button>
          {o.parentOrderId && (
            <span className="orden__marca orden__marca--aviso">{t('admin.ordenes.listado.retrabajo')}</span>
          )}
        </span>
      ),
      tarjeta: 'titulo',
    },
    {
      id: 'vehiculo',
      titulo: t('admin.ordenes.ficha.vehiculo'),
      // Todas las columnas con ancho, como en el listado de la caja: la que no lo
      // tiene se queda con lo que sobra, y en el celular eso era «Toyota…», sin la
      // placa —lo que más se busca— (#165). Sumadas caben en una laptop de 1366.
      ancho: 150,
      // La placa arriba, entera; la marca y el modelo debajo, en gris.
      celda: (o) => {
        const descripcion = descripcionDe({ brandName: o.assetBrandName, modelName: o.assetModelName });
        if (!o.assetIdentifier && !descripcion) return '—';
        return (
          <span className="listado-ordenes__vehiculo">
            <span>{o.assetIdentifier || descripcion}</span>
            {o.assetIdentifier && descripcion && <span className="listado__apagado">{descripcion}</span>}
          </span>
        );
      },
      // En la tarjeta, en un solo renglón: «P115-526 · Toyota Corolla».
      tarjeta: {
        lugar: 'renglon',
        celda: (o) =>
          [o.assetIdentifier, descripcionDe({ brandName: o.assetBrandName, modelName: o.assetModelName })]
            .filter(Boolean)
            .join(' · '),
      },
    },
    {
      id: 'cliente',
      titulo: t('admin.ordenes.ficha.cliente'),
      ancho: 160,
      celda: (o) =>
        o.customerId ? (
          (o.customerName ?? '—')
        ) : (
          <span className="listado__aviso">{t('admin.ordenes.ficha.sinCliente')}</span>
        ),
      tarjeta: 'renglon',
    },
    // Buscar cruza sucursales (ver `consultaDe`): solo entonces hace falta decir de cuál es.
    ...(filtros.buscar
      ? [
          {
            id: 'sucursal',
            titulo: t('admin.ordenes.listado.columna.sucursal'),
            ancho: 130,
            celda: (o: OrdenEnListado) => o.locationName ?? '—',
            // Al final del pie: sin `orden`, detrás del asignado y la entrega.
            tarjeta: 'pie' as const,
          },
        ]
      : []),
    {
      id: 'estado',
      titulo: t('admin.ordenes.listado.columna.estado'),
      ancho: 150,
      // El color del estado, el que eligió el taller: como en la ficha.
      celda: (o) => (
        <Chip>
          {o.statusColor && <span className="ficha__punto" style={{ background: o.statusColor }} aria-hidden="true" />}
          {o.statusName}
        </Chip>
      ),
      tarjeta: 'esquina',
    },
    {
      id: 'asignado',
      titulo: t('admin.ordenes.ficha.asignadoA'),
      ancho: 140,
      celda: (o) => o.assignedToName ?? <span className="listado__apagado">{t('admin.ordenes.sinAsignar')}</span>,
      tarjeta: { lugar: 'pie', orden: 1 },
    },
    {
      id: 'abierta',
      titulo: t('admin.ordenes.ficha.abierta'),
      ancho: 140,
      celda: (o) => <span className="listado__apagado">{fechaHora(o.openedAt, idioma)}</span>,
    },
    {
      id: 'entrega',
      titulo: t('admin.ordenes.listado.columna.entrega'),
      ancho: 150,
      celda: (o) => entrega(o, fechaHora),
      // En la tarjeta, solo el día: con la hora, el pie se recortaba antes de «Vencida».
      tarjeta: { lugar: 'pie', conTitulo: true, orden: 2, celda: (o) => entrega(o, fecha) },
    },
  ];

  /** La fecha prometida. Vencida solo mientras sigue en el taller: una entregada tarde ya no apura a nadie. */
  function entrega(o: OrdenEnListado, formato: typeof fecha) {
    if (!o.promisedAt) return '—';
    const enTaller = o.statusCategory !== 'closed' && o.statusCategory !== 'cancelled';
    const vencida = enTaller && new Date(o.promisedAt).getTime() < ahora;
    return (
      <span className={vencida ? 'listado-ordenes__vencida' : undefined}>
        {formato(o.promisedAt, idioma)}
        {vencida && ` · ${t('admin.ordenes.listado.vencida')}`}
      </span>
    );
  }

  const botonNueva = puede('order.edit') ? (
    <Boton icono={<IconoMas />} onClick={() => void navegar('/ordenes/nueva')}>
      {t('admin.ordenes.nueva.titulo')}
    </Boton>
  ) : null;

  const periodos: { valor: PeriodoListado; etiqueta: string }[] = [
    { valor: 'cualquiera', etiqueta: t('admin.ordenes.listado.periodo.cualquiera') },
    { valor: 'hoy', etiqueta: t('admin.ordenes.listado.periodo.hoy') },
    { valor: 'semana', etiqueta: t('admin.ordenes.listado.periodo.semana') },
    { valor: 'mes', etiqueta: t('admin.ordenes.listado.periodo.mes') },
  ];

  return (
    <Listado
      titulo={t('admin.ordenes.titulo')}
      info={t('admin.ordenes.listado.info')}
      columnas={columnas}
      filas={pagina?.items}
      claveFila={(o) => String(o.id)}
      estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
      onReintentar={() => void consulta.refetch()}
      // La ficha vuelve con «Atrás», y la URL trae los filtros como estaban.
      onFila={abrir}
      filtros={
        <>
          <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.ordenes.listado.buscar')} />
          {/* Buscando, el estado no se aplica (ver `consultaDe`): el combo dice lo que la
              tabla trae, «Todas», y no se toca. El de la URL sigue ahí para cuando se
              borre la búsqueda (#165). */}
          <Filtro
            etiqueta={t('admin.ordenes.listado.filtro.estado')}
            valor={filtros.buscar ? 'todos' : filtros.estado}
            deshabilitado={filtros.buscar !== ''}
            onCambiar={(estado) => filtrar({ estado })}
            opciones={[
              { valor: 'activas', etiqueta: t('admin.ordenes.listado.filtro.activas') },
              { valor: 'todos', etiqueta: t('admin.ordenes.listado.filtro.todas') },
              ...(estados.data ?? []).map((e) => ({ valor: String(e.id), etiqueta: e.name })),
            ]}
          />
          {puedeAsignar && (
            <Filtro
              etiqueta={t('admin.ordenes.listado.filtro.asignado')}
              valor={filtros.asignado}
              onCambiar={(asignado) => filtrar({ asignado })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.ordenes.listado.filtro.todos') },
                ...(asignables.data ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.name })),
              ]}
            />
          )}
          <Filtro
            etiqueta={t('admin.ordenes.listado.filtro.abierta')}
            valor={filtros.periodo}
            onCambiar={(periodo) => filtrar({ periodo: periodo as PeriodoListado })}
            opciones={periodos}
          />
        </>
      }
      {...(botonNueva && { acciones: botonNueva })}
      // «Activas» es el punto de partida, no un filtro puesto: no ofrece limpiarlo.
      hayFiltros={
        filtros.buscar !== '' ||
        filtros.estado !== FILTROS_INICIALES.estado ||
        filtros.asignado !== FILTROS_INICIALES.asignado ||
        filtros.periodo !== FILTROS_INICIALES.periodo ||
        filtros.cursor !== null
      }
      onLimpiarFiltros={() => {
        setTexto('');
        // La primera página aunque la URL ya estuviera limpia, como en Clientes.
        setCursor(null);
        setAnteriores([]);
        setParams({}, { replace: true });
      }}
      vacio={{
        titulo: t('admin.ordenes.listado.vacio.titulo'),
        texto: t('admin.ordenes.listado.vacio.texto'),
        ...(botonNueva && { acciones: botonNueva }),
      }}
      {...(filtros.buscar && { detalleSinResultados: t('admin.ordenes.listado.sinResultados', { texto: filtros.buscar }) })}
      paginacion={{
        hayAnterior: anteriores.length > 0,
        hayMas: pagina?.hasMore ?? false,
        onAnterior: () => {
          setCursor(anteriores.at(-1) ?? null);
          setAnteriores((a) => a.slice(0, -1));
        },
        onSiguiente: () => {
          if (!pagina?.nextCursor) return;
          setAnteriores((a) => [...a, cursor]);
          setCursor(pagina.nextCursor);
        },
      }}
    />
  );
}
